import * as React from "react";
import * as R from "remeda";
import { createStore, useStore } from "zustand";
import { devtools } from "zustand/middleware";

export interface ProtocolQuestionsListFilters {
  search: string | undefined;
  themesIds: number[];
  subThemesIds: number[];
}

export interface ProtocolQuestionsListQueryAdditionalArgs {
  requirementNot?: number;
  protocolQuestionNot?: number[];
}

export interface ProtocolQuestionsListUrlParts {
  id: number;
  protocolSetId: number;
}

export interface ProtocolQuestionsListState extends ProtocolQuestionsListFilters {
  checkedQuestions: number[];
  toggleCheckedQuestion: (value: number) => void;
  updateFilter: (
    key: keyof ProtocolQuestionsListFilters,
    value: string | boolean | number | number[] | undefined,
  ) => void;
  smartSearchOn: boolean;
  setSmartSearchOn: (value: boolean) => void;
  smartSearchThreshold: number;
  setSmartSearchThreshold: (value: number) => void;
  page: number;
  setPage: (value: number) => void;
  additionalQueryArgs: ProtocolQuestionsListQueryAdditionalArgs;
  urlParts: ProtocolQuestionsListUrlParts;
  onLinkCallback: () => void;
  clearCheckedQuestions: () => void;
}

interface CreateProtocolQuestionsListStoreProps {
  invalidateTags: () => void;
  additionalQueryArgs: ProtocolQuestionsListQueryAdditionalArgs;
  urlParts: ProtocolQuestionsListUrlParts;
  onLinkCallback: () => void;
}

/**
 * Create a store for the protocol questions linking page. This is used by:
 *  - requirements -> protocol questions
 *  - protocol questions -> protocol questions
 * linking.
 *
 * @param props
 * @param prevState The state depends on the loaded protocol question. Because it gets refreshed when new items are linked, our
 *              store needs to be able to restore the previous state to maintain user's filters
 */
export const createProtocolQuestionsListStore = (
  props: CreateProtocolQuestionsListStoreProps,
  prevState: ProtocolQuestionsListState | undefined,
) => {
  return createStore<ProtocolQuestionsListState>()(
    devtools(
      (set, get) => ({
        search: prevState?.search,
        themesIds: prevState?.themesIds ?? [],
        subThemesIds: prevState?.subThemesIds ?? [],
        smartSearchOn: prevState?.smartSearchOn ?? false,
        smartSearchThreshold: prevState?.smartSearchThreshold ?? 0,
        page: prevState?.page ?? 1,
        checkedQuestions: [],
        additionalQueryArgs: props.additionalQueryArgs,
        onLinkCallback: props.onLinkCallback,
        urlParts: props.urlParts,
        setPage: (value: number) => set({ page: value }),
        setSmartSearchThreshold: value => set({ smartSearchThreshold: value }),
        setSmartSearchOn: value => set({ smartSearchOn: value }),
        clearCheckedQuestions() {
          set({ checkedQuestions: [] });
        },
        toggleCheckedQuestion(value: number) {
          const prevState = get().checkedQuestions;
          const currentIndex = prevState.indexOf(value);
          const newChecked = [...prevState];

          if (currentIndex === -1) {
            newChecked.push(value);
          } else {
            newChecked.splice(currentIndex, 1);
          }
          set({ checkedQuestions: newChecked });
        },
        updateFilter: (
          key: keyof ProtocolQuestionsListFilters,
          value: string | boolean | number | number[] | undefined,
        ) => {
          // In order for rtk query merge logic to work with infinite scroll,
          // we need to invalidate the cache when the filter changes to ensure that
          // we don't use paginated data from the previous filter
          props.invalidateTags();

          // Since filters have updated, we need to reset the pagination
          set({ page: 1 });

          switch (key) {
            case "search":
              set({ search: value as string | undefined });
              break;
            case "themesIds":
              set({ themesIds: value as number[] });
              break;
            case "subThemesIds":
              set({ subThemesIds: value as number[] });
              break;
          }
        },
      }),
      { name: "ProtocolQuestionsListStore" },
    ),
  );
};

export type ProtocolQuestionsListStore = ReturnType<typeof createProtocolQuestionsListStore>;

export const CreateProtocolQuestionsListStoreContext = React.createContext<ProtocolQuestionsListStore | null>(null);

type CreateProtocolQuestionsListProviderProps = React.PropsWithChildren<CreateProtocolQuestionsListStoreProps>;

export function ProtocolQuestionsListProvider({ children, ...props }: CreateProtocolQuestionsListProviderProps) {
  const storeRef = React.useRef<ProtocolQuestionsListStore>(null);
  const propsRef = React.useRef<CreateProtocolQuestionsListStoreProps>(null);

  if (!R.isDeepEqual(propsRef.current, props)) {
    const previousState = storeRef.current?.getState();
    storeRef.current = createProtocolQuestionsListStore(props, previousState);
    propsRef.current = props;
  }

  return (
    <CreateProtocolQuestionsListStoreContext.Provider value={storeRef.current!}>
      {children}
    </CreateProtocolQuestionsListStoreContext.Provider>
  );
}

export function useProtocolQuestionsListStore<T>(selector: (state: ProtocolQuestionsListState) => T): T {
  const store = React.useContext(CreateProtocolQuestionsListStoreContext);
  if (!store) throw new Error("Missing CreateProtocolQuestionsListStoreContext.Provider in the tree");
  return useStore(store, selector);
}
